import { useCallback, useState } from 'react'
import { z } from 'zod'
import { errors } from '~/prix'
import { BaseActionDefinitionEnhanced } from '~/prix/actionDefinition'
import { AppError } from '../../error'
import useAPI from './api'
import objectHash from 'object-hash'
import { get, set } from '~/prix/utils/browserCache'
import { isDevelopment } from '~/utils/shared/platform'

export default function useActionFunction<
  ActionDefinition extends BaseActionDefinitionEnhanced<any, any>,
>(
  actionDefinition: ActionDefinition,
  {
    staleWhileRevalidate = false,
    sourceKey = 'default',
    initialIsLoading = true,
  }: {
    autoLoad?: boolean
    staleWhileRevalidate?: boolean
    sourceKey?: string
    initialIsLoading?: boolean
  } = {},
) {
  const [result, setResult] =
    useState<
      ActionDefinition extends BaseActionDefinitionEnhanced<any, infer O> ? z.infer<O> : any
    >()
  const [error, setError] = useState<Error | AppError | null>(null)
  const [isLoading, setIsLoading] = useState(initialIsLoading)

  const api = useAPI()
  const source = api.sources[sourceKey]

  const callAction = useCallback(
    (
      input: ActionDefinition extends BaseActionDefinitionEnhanced<infer I, any> ? z.infer<I> : any,
    ) => {
      if (!source) {
        throw errors.incompatible('Fonte de dados indisponível')
      }

      setError(null)
      setIsLoading(true)

      // Cache preparation
      const now = +new Date()
      const cache = actionDefinition.cache && !isDevelopment ? actionDefinition.cache(input) : null
      const hash =
        cache &&
        cache.front &&
        (cache.front.expirationMsForUpdate || cache.front.expirationMsForFallback)
          ? objectHash(input as any, {
              respectType: false,
            })
          : null
      const cacheName = hash ? `${actionDefinition.key}.${hash}` : null

      const cacheResultPromise: Promise<{
        value: any
        timestamp: number
        expirationTimestamp: number
      } | null> = cacheName ? get(cacheName) : Promise.resolve(null)

      let call: {
        run: () => Promise<any>
        abort: () => Promise<void>
      }

      const resultPromise = cacheResultPromise.then(cacheResult => {
        if (cacheResult !== null) {
          const isBestCache =
            cacheResult !== null &&
            cache!.front!.expirationMsForUpdate &&
            cacheResult.timestamp + cache!.front!.expirationMsForUpdate > now
          if (staleWhileRevalidate && !isBestCache) {
            console.log('Using stale cache while revalidating', cacheName, input)
            setResult(cacheResult.value)
            setIsLoading(false)
          } else if (isBestCache) {
            console.log('Using directly from cache', cacheName, input)
            setResult(cacheResult.value)
            setIsLoading(false)
            return cacheResult.value
          }
        }

        call = source.call(actionDefinition, input, api.context)
        return call
          .run()
          .then(result => {
            setResult(result)
            if (cacheName) {
              console.log('Saving to cache', cacheName, input, result)
              set(
                cacheName,
                result,
                Math.max(
                  cache!.front!.expirationMsForUpdate || 0,
                  cache!.front!.expirationMsForFallback || 0,
                ),
              ).catch(error => console.error(error))
            }
            return result
          })
          .catch(error => {
            if (
              cacheName &&
              cacheResult !== null &&
              cache!.front!.expirationMsForFallback &&
              cacheResult.timestamp + cache!.front!.expirationMsForFallback > now
            ) {
              console.error(error)
              console.log('Using fallback cache', cacheName, input)
              setResult(cacheResult!.value)
              return cacheResult!.value
            }

            setError(error)
            throw error
          })
          .finally(() => setIsLoading(false))
      })

      return {
        abort: () => call?.abort(),
        resultPromise,
      }
    },
    [source, api.context, actionDefinition],
  )

  return {
    result,
    error,
    isLoading,
    callAction,
  } as {
    result: ActionDefinition extends BaseActionDefinitionEnhanced<any, infer O>
      ? z.infer<O> | undefined
      : any
    error: Error | AppError | null
    isLoading: boolean
    callAction: (
      input: ActionDefinition extends BaseActionDefinitionEnhanced<infer I, any> ? z.infer<I> : any,
    ) => {
      abort: () => void
      resultPromise: Promise<
        ActionDefinition extends BaseActionDefinitionEnhanced<any, infer O> ? z.infer<O> : any
      >
    }
  }
}
